import './card.scss';
import {__} from '@wordpress/i18n';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {getSetting} from '@woocommerce/settings';
import {decodeEntities} from '@wordpress/html-entities';
import {useEffect, useRef, useState} from '@wordpress/element';
import {type CheckoutAfterProcessingWithErrorEventData} from '@woocommerce/checkout';
import {type PaymentMethodInterface} from '@woocommerce/types';
import {z} from '@peachpay/checkout-blocks';
import {
	createAcceptUIDataAttributes,
	createAcceptUIToken,
	getAcceptUIResponseError,
	isAcceptUITokenFresh,
	loadAcceptUIScript,
	type AcceptUIResponse,
	type AcceptUIToken,
} from './acceptui';
import {
	createAuthnetBlocksCheckoutFailResponse,
	createAuthnetBlocksPaymentMethodData,
} from './payment-data';

const SettingsSchema = z.object({
	icon: z.string(),
	title: z.string(),
	description: z.string(),
	supports: z.string().array(),
	authnet: z.object({
		login_id: z.string(),
		public_client_key: z.string(),
		accept_src: z.string(),
	}),
});

const parsedResult = SettingsSchema.safeParse(getSetting('peachpay_authnet_card_data'));
if (!parsedResult.success) {
	console.error('[PeachPay] Payment method "peachpay_authnet_card" disabled: invalid settings.', parsedResult.error);
}

const settings = parsedResult.success ? parsedResult.data : null;
const title = settings ? decodeEntities(settings.title) : '';
const description = settings ? decodeEntities(settings.description) : '';
const responseHandlerName = 'peachpayAuthnetBlocksResponseHandler';

declare global {
	interface Window {
		peachpayAuthnetBlocksResponseHandler?: (response: AcceptUIResponse) => void;
	}
}

function Label(props: PaymentMethodInterface) {
	const {PaymentMethodLabel, PaymentMethodIcons} = props.components;
	return (
		<>
			<PaymentMethodLabel text={title} />
			<PaymentMethodIcons className='pp-label-icon' icons={[{
				id: 'peachpay_authnet_card',
				src: settings?.icon ?? null,
				alt: title,
			}]} />
		</>
	);
}

function Content({eventRegistration, emitResponse}: PaymentMethodInterface) {
	const {onPaymentSetup, onCheckoutFail} = eventRegistration;
	const [token, setToken] = useState<AcceptUIToken | null>(null);
	const [error, setError] = useState<string>('');
	const [scriptReady, setScriptReady] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		window.peachpayAuthnetBlocksResponseHandler = (response: AcceptUIResponse) => {
			const responseError = getAcceptUIResponseError(response);
			if (responseError) {
				setToken(null);
				setError(responseError);
				return;
			}

			const nextToken = createAcceptUIToken(response);
			if (!nextToken) {
				setToken(null);
				setError(__('Card tokenization failed. Please try again.', 'peachpay-for-woocommerce'));
				return;
			}

			setToken(nextToken);
			setError('');
		};

		return () => {
			window.peachpayAuthnetBlocksResponseHandler = undefined;
		};
	}, []);

	useEffect(() => {
		if (!buttonRef.current || !settings?.authnet.accept_src) {
			return;
		}

		setScriptReady(false);
		loadAcceptUIScript(settings.authnet.accept_src)
			.then(() => {
				setScriptReady(true);
			})
			.catch(() => {
				setScriptReady(false);
				setError(__('Authorize.net hosted payment form failed to load. Please refresh and try again.', 'peachpay-for-woocommerce'));
			});
	}, []);

	useEffect(() => onPaymentSetup(async () => {
		if (!scriptReady) {
			return {
				type: emitResponse.responseTypes.ERROR,
				message: __('Authorize.net hosted payment form is still loading. Please try again.', 'peachpay-for-woocommerce'),
			};
		}

		if (!isAcceptUITokenFresh(token)) {
			setToken(null);
			return {
				type: emitResponse.responseTypes.ERROR,
				message: __('Please enter your card details before placing the order.', 'peachpay-for-woocommerce'),
			};
		}

		return {
			type: emitResponse.responseTypes.SUCCESS,
			meta: {
				paymentMethodData: createAuthnetBlocksPaymentMethodData(token.opaqueData),
			},
		};
	}), [onPaymentSetup, scriptReady, token]);

	useEffect(() => onCheckoutFail(async (data: CheckoutAfterProcessingWithErrorEventData) => {
		setToken(null);
		return createAuthnetBlocksCheckoutFailResponse(
			data,
			emitResponse,
			__('An unknown error occurred while processing the payment.', 'peachpay-for-woocommerce'),
		);
	}), [emitResponse, onCheckoutFail]);

	const buttonAttributes = settings ? createAcceptUIDataAttributes({
		apiLoginID: settings.authnet.login_id,
		clientKey: settings.authnet.public_client_key,
		responseHandler: responseHandlerName,
		buttonText: __('Submit', 'peachpay-for-woocommerce'),
		headerText: __('Card Information', 'peachpay-for-woocommerce'),
	}) : {};

	const cardLastFour = token?.last4?.replace(/\D/g, '').slice(-4);
	const cardSummary = cardLastFour && cardLastFour.length === 4
		? __('Card details entered.', 'peachpay-for-woocommerce') + ` •••• ${cardLastFour}`
		: __('Card details entered.', 'peachpay-for-woocommerce');

	return (
		<>
			{description ? <p className='pp-authnet-acceptui-description'>{description}</p> : null}
			<div className={`pp-authnet-acceptui${token ? ' is-tokenized' : ''}`}>
				<div className='pp-authnet-acceptui-action'>
					<button
						ref={buttonRef}
						type='button'
						className='AcceptUI pp-authnet-acceptui-button'
						disabled={!settings}
						aria-describedby='pp-authnet-acceptui-status'
						{...buttonAttributes}
					>
						<span className='pp-authnet-acceptui-button-icon' aria-hidden='true'>
							<svg viewBox='0 0 24 24' focusable='false'>
								<path d='M19 7h-1V6a6 6 0 0 0-12 0v1H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2Zm-3 0H8V6a4 4 0 0 1 8 0v1Zm-3 8.73V18h-2v-2.27A2 2 0 1 1 13 15.73Z' />
							</svg>
						</span>
						<span>{token ? __('Update card details', 'peachpay-for-woocommerce') : __('Enter card details', 'peachpay-for-woocommerce')}</span>
					</button>
					{token ? <span className='pp-authnet-acceptui-badge' aria-hidden='true'>{__('Securely saved', 'peachpay-for-woocommerce')}</span> : null}
				</div>
				<p id='pp-authnet-acceptui-status' className='pp-authnet-acceptui-status' aria-live='polite'>
					{token ? cardSummary : __('Card details open in a secure Authorize.net form and are not stored by this site.', 'peachpay-for-woocommerce')}
				</p>
				{error ? <p className='pp-authnet-error' role='alert'>{error}</p> : null}
			</div>
		</>
	);
}

if (settings) {
	registerPaymentMethod({
		name: 'peachpay_authnet_card',
		ariaLabel: title,
		canMakePayment(data) {
			return data.paymentMethods.includes('peachpay_authnet_card');
		},
		// @ts-expect-error WC will clone these components and pass the props when used.
		label: <Label />,
		// @ts-expect-error WC will clone these components and pass the props when used.
		content: <Content />,
		// @ts-expect-error WC will clone these components and pass the props when used.
		edit: <Content />,
		supports: {
			features: settings.supports,
		},
	});
}
